import React, { useRef, useState, useEffect } from 'react';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import {
  Box,
  Button,
  Paper,
  Alert,
  LinearProgress,
  Typography,
  Container,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
} from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import { Agreement, agreementService } from '../services/agreementService';
import { config } from '../config/environment';

interface AgreementSignProps {
  agreement: Agreement;
  onSigned: () => void;
}

// Signature fonts with their CSS font-family values
const SIGNATURE_FONTS = [
  { name: 'Bad Script', value: "'Bad Script', cursive", preview: 'Bad Script' },
  { name: 'Bilbo Swash Caps', value: "'Bilbo Swash Caps', cursive", preview: 'Bilbo Swash Caps' },
  { name: 'Caveat', value: "'Caveat', cursive", preview: 'Caveat' },
  { name: 'Covered By Your Grace', value: "'Covered By Your Grace', cursive", preview: 'Covered By Your Grace' },
  { name: 'Dancing Script', value: "'Dancing Script', cursive", preview: 'Dancing Script' },
  { name: 'Gochi Hand', value: "'Gochi Hand', cursive", preview: 'Gochi Hand' },
];

export const AgreementSign: React.FC<AgreementSignProps> = ({ agreement, onSigned }) => {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const textCanvas = useRef<HTMLCanvasElement>(null);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw');
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Load PDF URL if it's a PDF custom agreement
  useEffect(() => {
    if (agreement.type === 'pdf' && agreement.is_custom && agreement.custom_agreement_id) {
      const loadPdf = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${config.apiBaseUrl}/firm-custom-agreements/${agreement.custom_agreement_id}/download`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setPdfUrl(url);
          }
        } catch (err) {
          console.error('Failed to load PDF:', err);
        }
      };
      loadPdf();
    }
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [agreement.type, agreement.is_custom, agreement.custom_agreement_id]);

  // Draw typed text on canvas whenever it changes
  useEffect(() => {
    if (signatureMode === 'type' && textCanvas.current && typedName) {
      const canvas = textCanvas.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Set font and styling
      ctx.font = `48px ${selectedFont.value}`;
      ctx.fillStyle = '#000';
      ctx.textBaseline = 'middle';
      
      // Draw text centered
      const textWidth = ctx.measureText(typedName).width;
      const x = (canvas.width - textWidth) / 2;
      const y = canvas.height / 2;
      ctx.fillText(typedName, x, y);
    }
  }, [typedName, selectedFont, signatureMode]);

  const handleSign = async () => {
    if (!agreement) return;

    // Handle PDF custom agreements - just accept without signature
    if (agreement.type === 'pdf' && agreement.is_custom && agreement.custom_agreement_id) {
      try {
        setSigning(true);
        setError(null);
        await agreementService.acceptCustomAgreement({ custom_agreement_id: agreement.custom_agreement_id });
        onSigned();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to accept agreement. Please try again.');
      } finally {
        setSigning(false);
      }
      return;
    }

    // Handle HTML agreements (default or custom) - require signature
    let signatureData = '';

    if (signatureMode === 'draw') {
      if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
        setError('Please provide your signature before proceeding.');
        return;
      }
      signatureData = sigCanvas.current.toDataURL();
    } else {
      if (!typedName.trim()) {
        setError('Please type your name before proceeding.');
        return;
      }
      if (!textCanvas.current) {
        setError('Signature canvas not ready. Please try again.');
        return;
      }
      signatureData = textCanvas.current.toDataURL();
    }

    try {
      setSigning(true);
      setError(null);
      const signData: any = { agreement_id: agreement.id, signature_data: signatureData };
      if (agreement.is_custom && agreement.custom_agreement_id) {
        signData.custom_agreement_id = agreement.custom_agreement_id;
      }
      await agreementService.signAgreement(signData);
      onSigned();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to sign agreement. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  const handleClearSignature = () => {
    if (signatureMode === 'draw' && sigCanvas.current) {
      sigCanvas.current.clear();
    } else {
      setTypedName('');
    }
    setError(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card>
        <CardHeader title={agreement.title} subheader="Please review and sign to proceed." />
        <Divider />
        <CardContent>
          {agreement.type === 'pdf' && agreement.is_custom && pdfUrl ? (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={0} sx={{ p: 3, backgroundColor: '#fafafa', minHeight: '600px' }}>
                <iframe
                  src={pdfUrl}
                  style={{ width: '100%', height: '600px', border: 'none' }}
                  title="Service Agreement PDF"
                />
              </Paper>
            </Box>
          ) : agreement.type === 'pdf' && agreement.is_custom ? (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={0} sx={{ p: 3, backgroundColor: '#fafafa', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Paper>
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={0} sx={{ p: 3, backgroundColor: '#fafafa', '& *': { fontSize: '12px !important' } }}>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(agreement.html_content) }} />
              </Paper>
            </Box>
          )}

          {agreement.type === 'pdf' && agreement.is_custom ? (
            <Typography variant="h6" gutterBottom color="primary">
              Accept Agreement
            </Typography>
          ) : (
            <Typography variant="h6" gutterBottom color="primary">
              Electronic Signature
            </Typography>
          )}
          {agreement.type === 'pdf' && agreement.is_custom ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              By clicking "Accept Agreement" below, you acknowledge that you have read, understood, and agree to this agreement.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              By signing below, you acknowledge that you have read, understood, and agree to this agreement.
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {agreement.type === 'pdf' && agreement.is_custom ? (
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button onClick={handleSign} variant="contained" disabled={signing} size="large">
                {signing ? 'Accepting...' : 'Accept Agreement'}
              </Button>
            </Box>
          ) : (
            <>
              <Tabs
            value={signatureMode}
            onChange={(_, newValue) => {
              setSignatureMode(newValue);
              setError(null);
            }}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Draw Signature" value="draw" />
            <Tab label="Type Signature" value="type" />
          </Tabs>

          {signatureMode === 'draw' ? (
            <Paper elevation={1} sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, mb: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Please sign in the box below:
              </Typography>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, backgroundColor: 'white', maxWidth: 400 }}>
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{ width: 380, height: 140, style: { width: '380px', height: '140px' } }}
                  backgroundColor="white"
                />
              </Box>
              <Box sx={{ mt: 1, textAlign: 'right' }}>
                <Button size="small" onClick={handleClearSignature} disabled={signing}>
                  Clear Signature
                </Button>
              </Box>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, mb: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Type your name and choose a signature style:
              </Typography>
              
              <TextField
                fullWidth
                label="Type your full name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={signing}
                sx={{ mb: 2, maxWidth: 400 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose a signature style:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {SIGNATURE_FONTS.map((font) => (
                  <Button
                    key={font.name}
                    variant={selectedFont.name === font.name ? 'contained' : 'outlined'}
                    onClick={() => setSelectedFont(font)}
                    disabled={signing}
                    sx={{
                      fontFamily: font.value,
                      fontSize: '20px',
                      textTransform: 'none',
                      height: '60px',
                      flex: '1 1 calc(33.333% - 8px)',
                      minWidth: '150px',
                    }}
                  >
                    {typedName || font.preview}
                  </Button>
                ))}
              </Box>

              {typedName && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Preview:
                  </Typography>
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1, backgroundColor: 'white', maxWidth: 400, mb: 1 }}>
                    <canvas
                      ref={textCanvas}
                      width={380}
                      height={140}
                      style={{ width: '380px', height: '140px', display: 'block' }}
                    />
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Button size="small" onClick={handleClearSignature} disabled={signing}>
                      Clear
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          )}

              {signing && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Processing your signature...
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={handleSign} variant="contained" disabled={signing}>
                  {signing ? 'Signing...' : 'Sign Agreement'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};
